import React from "react";
import { Navigate, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import DashboardLayout from "../../components/DashboardLayout";
import ModalRoute from "../../components/common/ModalRoute";
import ApiAccessEmptyState from "../../components/api/ApiAccessEmptyState";
import ApiPlayground from "../../components/api/ApiPlayground";
import ApiRequestDetail from "../../components/api/ApiRequestDetail";
import ApiUsageCharts from "../../components/api/ApiUsageCharts";
import AgentInstructionsModal from "../../components/api/AgentInstructionsModal";
import { RobotHeadIcon } from "../../components/api/ApiHelpLinks";

const Api = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentBrand, brands } = useSelector((state) => state.brand);
  const { currentProject } = useSelector((state) => state.project);
  const hasApi = Boolean(currentUser?.features?.api);

  if (!hasApi) {
    return (
      <DashboardLayout title="API" currentUser={currentUser}>
        <ApiAccessEmptyState />
      </DashboardLayout>
    );
  }

  const activeTab = location.pathname.includes("/usage") ? "usage" : "playground";
  const navConfig = [
    {
      id: "playground",
      label: "Playground",
      to: "/api/playground/",
      active: activeTab === "playground",
    },
    {
      id: "usage",
      label: "Usage",
      to: "/api/usage/",
      active: activeTab === "usage",
    },
  ];

  const primaryAction = (
    <button
      type="button"
      onClick={() => navigate("/api/integrate/")}
      className="inline-flex items-center gap-2 rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button"
      style={{ backgroundColor: "var(--theme-primary)", color: "var(--theme-button-text)" }}
    >
      <RobotHeadIcon className="h-4 w-4" />
      Agent Instructions
    </button>
  );

  return (
    <DashboardLayout
      title="API"
      currentUser={currentUser}
      navConfig={navConfig}
      primaryAction={primaryAction}
    >
      <Routes>
        <Route
          path="playground/*"
          element={
            <ApiPlayground
              context="brand"
              currentBrand={currentBrand}
              brands={brands}
              projectHash={currentProject?.hash || currentBrand?.project_hash}
            />
          }
        />
        <Route
          path="usage/*"
          element={
            <ApiUsageCharts
              layout="page"
              currentUser={currentUser}
              onSelectRequest={(row) => navigate(`/api/usage/${row.hash}/`)}
            />
          }
        />
        {/* Keep playground behind the integrate modal overlay */}
        <Route
          path="integrate"
          element={
            <ApiPlayground
              context="brand"
              currentBrand={currentBrand}
              brands={brands}
              projectHash={currentProject?.hash || currentBrand?.project_hash}
            />
          }
        />
        <Route path="" element={<Navigate to="playground/" replace />} />
        <Route path="*" element={<Navigate to="playground/" replace />} />
      </Routes>
      <ModalRoute
        path="/api/usage/:hash"
        parentPath="/api/usage/"
        element={<ApiRequestDetailWrapper />}
        maxWidth="max-w-3xl"
        heightClass="h-[85vh] max-h-[85vh]"
        noPadding
      />
      <ModalRoute
        path="/api/integrate"
        parentPath="/api/playground/"
        element={
          <AgentInstructionsModal onClose={() => navigate("/api/playground/")} />
        }
        maxWidth="max-w-2xl"
        heightClass="h-[90vh] max-h-[90vh]"
        noPadding
      />
      <ModalRoute
        path="/api/playground/agent-instructions"
        parentPath="/api/playground/"
        element={
          <AgentInstructionsModal onClose={() => navigate("/api/playground/")} />
        }
        maxWidth="max-w-2xl"
        heightClass="h-[90vh] max-h-[90vh]"
        noPadding
      />
    </DashboardLayout>
  );
};

function ApiRequestDetailWrapper() {
  const match = useLocation().pathname.match(/\/api\/usage\/([^/]+)/);
  const hash = match?.[1];
  if (!hash) return null;
  return <ApiRequestDetail hash={hash} />;
}

export default Api;
